/**
 * A single-date picker: a trigger showing the picked date, opening a popover with a masked DD/MM/YYYY
 * text input (type continuous digits — "26062026" auto-formats to 26/06/2026) and a month calendar with
 * month AND year navigation. NO native <input type="date"> (its OS calendar — Thai Buddhist-era, no less
 * — is exactly the landmine DateRangePicker was built to dodge, and until 31/7 this was the one place in
 * the app still stepping on it). Dates are ISO "YYYY-MM-DD" on the wire (`value` is `""` for "no date
 * chosen"); min/max clamp.
 *
 * Built from DateRangePicker's internals — see dateFieldShared.tsx — rather than copy-pasted: the masked
 * input, the fixed-popover mechanics (why fixed and not portalled: dateFieldShared.tsx's own comment), and
 * the calendar grid's nav/keyboard handling are byte-identical needs between a range and a single date.
 * What's genuinely different for one date instead of two: no anchor/two-click selection (one click both
 * picks AND closes the popover — there's nothing left to decide once a single value is set), and no
 * From/To pair of typed fields, just one.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, isSameDay, isValid, parseISO, startOfMonth } from "date-fns";
import { CalendarDays } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  CalendarHeader,
  WEEKDAYS,
  iso,
  isoToDMY,
  maskDigits,
  prettyIso,
  useCalendarNav,
  useFixedPopover,
} from "./dateFieldShared";

export interface DatePickerProps {
  value: string; // ISO YYYY-MM-DD, or "" for no date chosen
  onChange: (value: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  className?: string;
  /** Accessible name for the trigger + popover (the visible field label sits outside the widget). */
  "aria-label"?: string;
}

export function DatePicker({ value, onChange, min, max, className, ...aria }: DatePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const coords = useFixedPopover({
    open,
    onClose: () => setOpen(false),
    containerRef: ref,
    triggerRef,
    popoverRef,
  });

  function commit(next: string) {
    onChange(next);
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <div ref={ref} className={cn("relative inline-flex", className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={aria["aria-label"]}
        className={cn(
          "inline-flex h-9 items-center gap-2 rounded-(--radius-sm) border border-(--border-input) bg-(--surface) px-3 text-(length:--text-sm) text-(--text)",
          "transition-colors hover:border-(--border-strong) focus-visible:border-(--brand) focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--ring)]",
        )}
      >
        <CalendarDays className="size-4 shrink-0 text-(--text-subtle)" aria-hidden />
        <span className="tabular">{value ? prettyIso(value) : t("datepicker.placeholder")}</span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={aria["aria-label"] ?? t("datepicker.label")}
          // Same non-onBlur close story as DateRangePicker (see its own comment): a disabled day fires no
          // mousedown yet still bounces focus outside the widget, so closing is owned entirely by the
          // document mousedown-outside listener + Escape + the trigger toggle inside useFixedPopover.
          className="fixed z-(--z-dropdown) flex flex-col gap-3 rounded-(--radius-lg) border border-(--border) bg-(--surface-raised) p-4 shadow-(--shadow-lg)"
          style={coords ? { top: coords.top, left: coords.left } : { visibility: "hidden" }}
        >
          <TypedInput value={value} onCommit={commit} min={min} max={max} />
          <Calendar value={value} onPick={commit} min={min} max={max} />
        </div>
      )}
    </div>
  );
}

/** Masked DD/MM/YYYY text input — type digits straight through; commits on a complete, valid date (via
 *  the SAME `commit` the calendar click uses, so typing and clicking a day behave identically — close +
 *  return focus to the trigger). Shows an inline aria-live hint when a complete entry is invalid / out of
 *  range, same as DateRangePicker's typed fields. */
function TypedInput({
  value,
  onCommit,
  min,
  max,
}: {
  value: string;
  onCommit: (v: string) => void;
  min?: string | undefined;
  max?: string | undefined;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => isoToDMY(value));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setText(isoToDMY(value)), [value]);

  function maskAndCommit(raw: string) {
    const { formatted, digits } = maskDigits(raw);
    setText(formatted);
    if (digits.length < 8) {
      setError(null);
      return;
    }
    const isoStr = `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
    const d = parseISO(isoStr);
    if (!isValid(d) || iso(d) !== isoStr) {
      setError(t("datepicker.invalid"));
      return;
    }
    if ((min && isoStr < min) || (max && isoStr > max)) {
      // Out-of-range typed dates are REJECTED, not silently clamped — a clamp would commit a value
      // the operator never typed and close the popover before the warning could paint (SEAM-3,
      // adversarial review 31/7). Same shape as the invalid-date branch above: don't commit, don't
      // close, just say why. The operator keeps their typed text and can correct it; a save gate
      // (e.g. validateRenewYes) still blocks while the committed value stays unset/stale. Calendar
      // picks are unaffected — an out-of-range day is `disabled` and unclickable, so this path is
      // reachable only via typed input.
      setError(t("datepicker.outOfRange"));
      return;
    }
    setError(null);
    onCommit(isoStr);
  }

  const inputCls =
    "h-8 w-28 rounded-(--radius-sm) border border-(--border-input) bg-(--surface) px-2 text-(length:--text-sm) tabular text-(--text) focus-visible:border-(--brand) focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--ring)]";

  return (
    <div className="flex flex-col gap-1.5">
      <input
        inputMode="numeric"
        placeholder="DD/MM/YYYY"
        value={text}
        onChange={(e) => maskAndCommit(e.target.value)}
        className={inputCls}
      />
      {error && (
        <p role="alert" className="text-(length:--text-2xs) text-(--danger-strong-fg)">
          {error}
        </p>
      )}
    </div>
  );
}

function Calendar({
  value,
  onPick,
  min,
  max,
}: {
  value: string;
  onPick: (v: string) => void;
  min?: string | undefined;
  max?: string | undefined;
}) {
  const selD = parseISO(value);
  const minD = parseISO(min ?? "");
  // Open on the picked date if there is one; otherwise on `min` (near the allowed window) rather than
  // today — the same landing-in-a-fully-disabled-month bug DateRangePicker's Calendar comment describes,
  // reachable here too whenever `min` is well in the future of today (e.g. RenewDecisionFields' nextEnd,
  // whose `min` is the day after the current period ends).
  const initialMonth = isValid(selD) ? selD : isValid(minD) ? minD : new Date();
  const [month, setMonth] = useState(() => startOfMonth(initialMonth));
  const { days, focusedDate, setFocusedDate, gridRef, onGridKeyDown } = useCalendarNav({
    month,
    setMonth,
    min,
    max,
    initialFocusedDate: initialMonth,
  });

  function pick(day: Date) {
    const s = iso(day);
    if (min && s < min) return;
    if (max && s > max) return;
    onPick(s);
  }

  return (
    <div className="flex w-[16rem] flex-col gap-2">
      <CalendarHeader month={month} setMonth={setMonth} />

      <div ref={gridRef} onKeyDown={onGridKeyDown} className="grid grid-cols-7 gap-0.5">
        {WEEKDAYS.map((d, i) => (
          <span
            key={i}
            className="grid h-7 place-items-center text-(length:--text-2xs) font-medium text-(--text-subtle)"
          >
            {d}
          </span>
        ))}
        {days.map((day) => {
          const inMonth = day.getMonth() === month.getMonth();
          const s = iso(day);
          const disabled = (min && s < min) || (max && s > max);
          const isSelected = isValid(selD) && isSameDay(day, selD);
          const isFocusTarget = isSameDay(day, focusedDate);
          return (
            <button
              type="button"
              key={s}
              data-day={s}
              disabled={!!disabled}
              tabIndex={isFocusTarget ? 0 : -1}
              onClick={() => pick(day)}
              onFocus={() => {
                if (!isSameDay(day, focusedDate)) setFocusedDate(day);
              }}
              aria-label={format(day, "MMMM d, yyyy")}
              aria-pressed={isSelected}
              aria-selected={isSelected}
              {...(isSelected ? { "aria-current": "date" as const } : {})}
              className={cn(
                "grid h-8 w-8 place-items-center rounded-(--radius-sm) text-(length:--text-xs) tabular transition-colors",
                disabled && "cursor-not-allowed opacity-30",
                !inMonth && "text-(--text-subtle)",
                inMonth && !isSelected && "text-(--text) hover:bg-(--surface-hover)",
                isSelected && "bg-(--brand) font-semibold text-(--text-onbrand)",
              )}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
